/**
 * ProjetsTachesV2 — Poste de pilotage de la production (ep:tasks).
 *
 * Modèle unifié : la TÂCHE est l'unité de travail unique.
 *   - entrée  : une demande club (ep:requests) engendre une tâche (requestId / taskId)
 *   - projet  : la CAMPAGNE (ep:campagnes) regroupe ses tâches via campagneId
 *   - qui     : assignedTo[] (ep:users) ·  périmètre : clubs[] (ep:clubs)
 * Aucun objet « projet » distinct : la campagne EST le projet.
 *
 * 3 vues sur le même jeu filtré : par statut (board) · par personne · par campagne.
 * Droits : roles.js (canProduce travailler · canManage/canAttribute créer-réassigner).
 */
import React, { useState, useMemo, useCallback } from "react";
import { useTasks, useRequests, useCampagnes, useUsers, useClubs } from "../hooks/useV1Data";
import { createTask, updateTask, setTaskStatus, toggleSubtask } from "../hooks/useV1Write";
import { isAdmin, canProduce, scopeClubs } from "../lib/roles";

/* ── Tokens (alignés sur DemandesV2) ── */
const STATUSES = ["À faire", "En cours", "En validation", "Terminée", "Bloquée"];
const T_STATUS = {
  "À faire":       { bg: "#EFF6FF", text: "#0071CD", dot: "#0071CD" },
  "En cours":      { bg: "#DBEAFE", text: "#0071CD", dot: "#2563EB" },
  "En validation": { bg: "#F3E8FF", text: "#6D28D9", dot: "#8B5CF6" },
  "Terminée":      { bg: "#ECFDF5", text: "#065F46", dot: "#10B981" },
  "Bloquée":       { bg: "#FEF2F2", text: "#991B1B", dot: "#EF4444" },
};
const PRIORITIES = ["Basse", "Normale", "Haute", "Urgente"];
const T_PRIORITY = { "Basse": "#64748B", "Normale": "#0071CD", "Haute": "#D97706", "Urgente": "#B23B3B" };
const ACCENT = "#0071CD";
const FR_MONTHS_S = ["jan.", "fév.", "mar.", "avr.", "mai", "jui.", "jul.", "aoû.", "sep.", "oct.", "nov.", "déc."];

/* ── Helpers (repris de DemandesV2) ── */
function startOfDay(d) { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; }
function parseDate(s) {
  if (!s) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;
  if (s && typeof s === "object" && s.toDate) return s.toDate();
  if (s && typeof s === "object" && s.seconds) return new Date(s.seconds * 1000);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function fmtDateShort(s) {
  const d = parseDate(s);
  if (!d) return null;
  return `${d.getDate()} ${FR_MONTHS_S[d.getMonth()]}`;
}
function userLabel(u) {
  if (!u) return "";
  return (
    u.name || u.displayName ||
    [u.prenom, u.nom].filter(Boolean).join(" ") ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email || ""
  );
}
function initials(name) {
  if (!name) return "?";
  const parts = String(name).split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0][0] || "?").toUpperCase();
}
function normStatus(s) {
  const v = String(s || "").toLowerCase().trim();
  if (v.startsWith("termin") || v === "done" || v === "completed") return "Terminée";
  if (v.includes("valid")) return "En validation";
  if (v.includes("cours") || v === "in progress") return "En cours";
  if (v.startsWith("bloq") || v === "blocked") return "Bloquée";
  return "À faire";
}
function clubIdsOf(entity) {
  const arr = Array.isArray(entity && entity.clubs) ? entity.clubs.map(String) : [];
  if (entity && entity.club != null && entity.club !== "" && !arr.includes(String(entity.club))) {
    arr.push(String(entity.club));
  }
  return arr;
}
function meId(user) {
  const v = user && (user.appId != null ? user.appId : user.id);
  return v == null ? "" : String(v);
}
function taskProgress(task) {
  const subs = (task && task.subtasks) || [];
  if (!subs.length) return null;
  const done = subs.filter((s) => s.done).length;
  return { done, total: subs.length, pct: Math.round((done / subs.length) * 100) };
}

/* ════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════ */
export default function ProjetsTachesV2({ appId, currentUser, onNavigate, initialSelectedCampagneId }) {
  const { tasks, loading: loadingTasks } = useTasks();
  const { requests } = useRequests();
  const { campagnes } = useCampagnes();
  const { users } = useUsers();
  const { clubs } = useClubs();

  const [view, setView]           = useState("statut"); // statut | personne | campagne
  const [fClub, setFClub]         = useState("");
  const [fCampagne, setFCampagne] = useState(initialSelectedCampagneId ? String(initialSelectedCampagneId) : "");
  const [fAssignee, setFAssignee] = useState("");
  const [search, setSearch]       = useState("");
  const [editing, setEditing]     = useState(null); // tâche en cours d'édition, ou {} pour création

  const admin   = isAdmin(currentUser);
  const produce = canProduce(currentUser);
  const scope   = useMemo(() => scopeClubs(currentUser), [currentUser]);
  const me       = meId(currentUser);

  /* ── Index de résolution ── */
  const userById = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      if (u == null) return;
      if (u.id != null) m.set(String(u.id), u);
      if (u.appId != null) m.set(String(u.appId), u);
    });
    return m;
  }, [users]);

  const clubById = useMemo(() => {
    const m = new Map();
    (clubs || []).forEach((c) => c && c.id != null && m.set(String(c.id), c));
    return m;
  }, [clubs]);
  const clubName = useCallback((id) => {
    const c = clubById.get(String(id));
    if (c) return c.name || c.nom || `Club ${id}`;
    const known = { "1": "Saint-Priest", "2": "La Boisse", "3": "Mâcon" };
    return known[String(id)] || `Club ${id}`;
  }, [clubById]);

  const campById = useMemo(() => {
    const m = new Map();
    (campagnes || []).forEach((c) => c && c.id != null && m.set(String(c.id), c));
    return m;
  }, [campagnes]);

  const requestByTaskId = useMemo(() => {
    const m = new Map();
    (requests || []).forEach((r) => r && r.taskId != null && m.set(String(r.taskId), r));
    return m;
  }, [requests]);

  /* Équipe triée (pour filtres + modale) */
  const team = useMemo(
    () => (users || []).filter(Boolean).slice().sort((a, b) => userLabel(a).localeCompare(userLabel(b))),
    [users]
  );
  /* Campagnes publiées (étiquette projet) */
  const campList = useMemo(
    () => (campagnes || []).filter((c) => c && c.name).slice().sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [campagnes]
  );
  /* Clubs du périmètre pour les filtres (admin = tous) */
  const clubOptions = useMemo(() => {
    const all = (clubs || []).filter((c) => c && c.id != null);
    const list = admin ? all : all.filter((c) => scope.includes(String(c.id)));
    return list.map((c) => ({ id: String(c.id), name: c.name || c.nom || `Club ${c.id}` }));
  }, [clubs, admin, scope]);

  /* ── Tâches visibles (périmètre) + enrichissement ── */
  const canViewTask = useCallback((t) => {
    if (admin) return true;
    if (!t) return false;
    const assigned = Array.isArray(t.assignedTo) ? t.assignedTo.map(String) : [];
    if (me && (assigned.includes(me) || String(t.assigneeId) === me)) return true;
    const tc = clubIdsOf(t);
    return tc.some((id) => scope.includes(id));
  }, [admin, me, scope]);

  const enriched = useMemo(() => {
    return (tasks || [])
      .filter(Boolean)
      .filter(canViewTask)
      .map((t) => {
        const assigneeIds = Array.isArray(t.assignedTo) && t.assignedTo.length
          ? t.assignedTo.map(String)
          : (t.assigneeId != null ? [String(t.assigneeId)] : []);
        const assigneeNames = assigneeIds
          .map((id) => userLabel(userById.get(id)))
          .filter(Boolean);
        const camp = t.campagneId ? campById.get(String(t.campagneId)) : null;
        const origin = requestByTaskId.get(String(t.id)) || null;
        return {
          raw: t,
          id: String(t.id),
          title: t.title || t.label || "Tâche",
          status: normStatus(t.status),
          priority: PRIORITIES.includes(t.priority) ? t.priority : "Normale",
          deadline: t.deadline || t.dueDate || t.date || "",
          clubIds: clubIdsOf(t),
          assigneeIds,
          assigneeNames,
          campagneId: t.campagneId ? String(t.campagneId) : "",
          campagneName: camp ? camp.name : "",
          origin,
          progress: taskProgress(t),
        };
      });
  }, [tasks, canViewTask, userById, campById, requestByTaskId]);

  /* ── Filtres ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((t) => {
      if (fClub && !t.clubIds.includes(String(fClub))) return false;
      if (fCampagne && t.campagneId !== String(fCampagne)) return false;
      if (fAssignee) {
        if (fAssignee === "__none__") { if (t.assigneeIds.length) return false; }
        else if (!t.assigneeIds.includes(String(fAssignee))) return false;
      }
      if (q) {
        const hay = `${t.title} ${t.campagneName} ${t.assigneeNames.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, fClub, fCampagne, fAssignee, search]);

  /* ── Changement de statut inline ── */
  const onStatusChange = useCallback(async (id, status) => {
    try { await setTaskStatus(id, status, appId); }
    catch { /* le onSnapshot resynchronise ; silencieux */ }
  }, [appId]);

  const onToggleSub = useCallback(async (taskId, subId) => {
    try { await toggleSubtask(taskId, subId, appId); }
    catch { /* silencieux */ }
  }, [appId]);

  const resetFilters = () => { setFClub(""); setFCampagne(""); setFAssignee(""); setSearch(""); };
  const hasFilters = fClub || fCampagne || fAssignee || search;

  /* ════════ Rendu ════════ */
  return (
    <div className="pt-page">
      {/* Barre d'outils */}
      <div className="pt-toolbar">
        <div className="pt-views">
          {[["statut", "Par statut"], ["personne", "Par personne"], ["campagne", "Par campagne"]].map(([v, lbl]) => (
            <button
              key={v}
              className={`pt-viewbtn${view === v ? " is-active" : ""}`}
              onClick={() => setView(v)}
            >{lbl}</button>
          ))}
        </div>

        <div className="pt-filters">
          <input
            className="pt-input"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {clubOptions.length > 1 && (
            <select className="pt-input" value={fClub} onChange={(e) => setFClub(e.target.value)}>
              <option value="">Tous les clubs</option>
              {clubOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select className="pt-input" value={fCampagne} onChange={(e) => setFCampagne(e.target.value)}>
            <option value="">Toutes campagnes</option>
            {campList.map((c) => <option key={String(c.id)} value={String(c.id)}>{c.name}</option>)}
          </select>
          <select className="pt-input" value={fAssignee} onChange={(e) => setFAssignee(e.target.value)}>
            <option value="">Tout le monde</option>
            <option value="__none__">Non assignée</option>
            {team.map((u) => {
              const id = u.id != null ? u.id : u.appId;
              return <option key={String(id)} value={String(id)}>{userLabel(u)}</option>;
            })}
          </select>
          {hasFilters && <button className="pt-link" onClick={resetFilters}>Réinitialiser</button>}
          {produce && (
            <button className="pt-newbtn" onClick={() => setEditing({})}>+ Tâche</button>
          )}
        </div>
      </div>

      {/* Contenu */}
      {loadingTasks ? (
        <div className="pt-empty">Chargement des tâches…</div>
      ) : filtered.length === 0 ? (
        <div className="pt-empty">
          {hasFilters ? "Aucune tâche ne correspond à ces filtres." : "Aucune tâche pour le moment."}
          {produce && !hasFilters && (
            <div style={{ marginTop: 12 }}>
              <button className="pt-newbtn" onClick={() => setEditing({})}>Créer la première tâche</button>
            </div>
          )}
        </div>
      ) : view === "statut" ? (
        <BoardByStatus
          tasks={filtered}
          onEdit={setEditing}
          onStatusChange={onStatusChange}
          onToggleSub={onToggleSub}
          onNavigate={onNavigate}
          canProduce={produce}
          clubName={clubName}
        />
      ) : view === "personne" ? (
        <GroupedList
          tasks={filtered}
          groupBy="personne"
          team={team}
          onEdit={setEditing}
          onStatusChange={onStatusChange}
          onToggleSub={onToggleSub}
          onNavigate={onNavigate}
          canProduce={produce}
          clubName={clubName}
        />
      ) : (
        <GroupedList
          tasks={filtered}
          groupBy="campagne"
          campList={campList}
          onEdit={setEditing}
          onStatusChange={onStatusChange}
          onToggleSub={onToggleSub}
          onNavigate={onNavigate}
          canProduce={produce}
          clubName={clubName}
        />
      )}

      {editing && (
        <TaskModal
          task={editing.id ? editing.raw : null}
          appId={appId}
          team={team}
          campList={campList}
          clubOptions={clubOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   VUE PAR STATUT (board)
   ════════════════════════════════════════════════════════ */
function BoardByStatus({ tasks, onEdit, onStatusChange, onToggleSub, onNavigate, canProduce, clubName }) {
  const byStatus = useMemo(() => {
    const m = {};
    STATUSES.forEach((s) => (m[s] = []));
    tasks.forEach((t) => { (m[t.status] || m["À faire"]).push(t); });
    return m;
  }, [tasks]);

  return (
    <div className="pt-board">
      {STATUSES.map((s) => (
        <div className="pt-col" key={s}>
          <div className="pt-col-head">
            <span className="pt-dot" style={{ background: T_STATUS[s].dot }} />
            <span className="pt-col-title">{s}</span>
            <span className="pt-count">{byStatus[s].length}</span>
          </div>
          <div className="pt-col-body">
            {byStatus[s].map((t) => (
              <TaskCard
                key={t.id}
                t={t}
                onEdit={onEdit}
                onStatusChange={onStatusChange}
                onToggleSub={onToggleSub}
                onNavigate={onNavigate}
                canProduce={canProduce}
                clubName={clubName}
                showStatus={false}
              />
            ))}
            {byStatus[s].length === 0 && <div className="pt-col-empty">—</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   VUES GROUPÉES (par personne / par campagne)
   ════════════════════════════════════════════════════════ */
function GroupedList({ tasks, groupBy, team, campList, onEdit, onStatusChange, onToggleSub, onNavigate, canProduce, clubName }) {
  const groups = useMemo(() => {
    const map = new Map();
    const push = (key, label, t) => {
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(t);
    };
    tasks.forEach((t) => {
      if (groupBy === "personne") {
        if (!t.assigneeIds.length) push("__none__", "Non assignée", t);
        else t.assigneeIds.forEach((id, i) => push(id, t.assigneeNames[i] || "Inconnu", t));
      } else {
        if (!t.campagneId) push("__none__", "Sans campagne", t);
        else push(t.campagneId, t.campagneName || "Campagne", t);
      }
    });
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      return a.label.localeCompare(b.label);
    });
    return arr;
  }, [tasks, groupBy]);

  return (
    <div className="pt-groups">
      {groups.map((g) => (
        <section className="pt-group" key={g.key}>
          <header className="pt-group-head">
            {groupBy === "personne" && g.key !== "__none__" && (
              <span className="pt-avatar">{initials(g.label)}</span>
            )}
            <h3 className="pt-group-title">{g.label}</h3>
            <span className="pt-count">{g.items.length}</span>
            {groupBy === "campagne" && g.key !== "__none__" && onNavigate && (
              <button className="pt-link" onClick={() => onNavigate("campagnes")}>Voir la campagne →</button>
            )}
          </header>
          <div className="pt-group-body">
            {g.items.map((t) => (
              <TaskCard
                key={`${g.key}-${t.id}`}
                t={t}
                onEdit={onEdit}
                onStatusChange={onStatusChange}
                onToggleSub={onToggleSub}
                onNavigate={onNavigate}
                canProduce={canProduce}
                clubName={clubName}
                showStatus={true}
                wide
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   CARTE TÂCHE
   ════════════════════════════════════════════════════════ */
function TaskCard({ t, onEdit, onStatusChange, onToggleSub, onNavigate, canProduce, clubName, showStatus, wide }) {
  const today = startOfDay(new Date());
  const dl = parseDate(t.deadline);
  const late = dl && startOfDay(dl) < today && t.status !== "Terminée";
  const soon = dl && !late && startOfDay(dl) <= new Date(today.getTime() + 7 * 864e5) && t.status !== "Terminée";

  return (
    <div className={`pt-card${wide ? " pt-card--wide" : ""}`}>
      <div className="pt-card-top">
        <button className="pt-card-title" onClick={() => canProduce && onEdit(t)} title={canProduce ? "Modifier" : undefined}>
          {t.title}
        </button>
        <span className="pt-pri" style={{ color: T_PRIORITY[t.priority], borderColor: T_PRIORITY[t.priority] }}>
          {t.priority}
        </span>
      </div>

      <div className="pt-meta">
        {t.campagneName && (
          <button
            className="pt-chip pt-chip--camp"
            onClick={() => onNavigate && onNavigate("campagnes")}
            title="Voir la campagne"
          >◆ {t.campagneName}</button>
        )}
        {t.clubIds.map((id) => (
          <span className="pt-chip" key={id}>{clubName(id)}</span>
        ))}
        {t.deadline && (
          <span className={`pt-chip pt-chip--date${late ? " is-late" : soon ? " is-soon" : ""}`}>
            {late ? "En retard · " : ""}{fmtDateShort(t.deadline)}
          </span>
        )}
      </div>

      {t.progress && (
        <div className="pt-prog">
          <div className="pt-prog-bar"><span style={{ width: `${t.progress.pct}%` }} /></div>
          <span className="pt-prog-lbl">{t.progress.done}/{t.progress.total}</span>
        </div>
      )}

      {t.raw.subtasks && t.raw.subtasks.length > 0 && (
        <ul className="pt-subs">
          {t.raw.subtasks.slice(0, 4).map((s) => (
            <li key={String(s.id)}>
              <label>
                <input
                  type="checkbox"
                  checked={!!s.done}
                  disabled={!canProduce}
                  onChange={() => onToggleSub(t.id, s.id)}
                />
                <span className={s.done ? "is-done" : ""}>{s.label || s.title || "Sous-tâche"}</span>
              </label>
            </li>
          ))}
          {t.raw.subtasks.length > 4 && <li className="pt-subs-more">+{t.raw.subtasks.length - 4} autres</li>}
        </ul>
      )}

      <div className="pt-card-foot">
        {t.assigneeNames.length > 0 ? (
          <span className="pt-assignees">
            {t.assigneeNames.slice(0, 3).map((n, i) => (
              <span className="pt-avatar pt-avatar--sm" key={i} title={n}>{initials(n)}</span>
            ))}
          </span>
        ) : (
          <span className="pt-unassigned">Non assignée</span>
        )}

        <span className="pt-foot-right">
          {t.origin && onNavigate && (
            <button className="pt-link" onClick={() => onNavigate("demandes")} title="Voir la demande d'origine">
              ↩ Demande
            </button>
          )}
          {showStatus ? (
            canProduce ? (
              <select
                className="pt-status-sel"
                value={t.status}
                onChange={(e) => onStatusChange(t.id, e.target.value)}
                style={{ color: T_STATUS[t.status].text, background: T_STATUS[t.status].bg }}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <span className="pt-status-badge" style={{ color: T_STATUS[t.status].text, background: T_STATUS[t.status].bg }}>
                {t.status}
              </span>
            )
          ) : canProduce ? (
            <select
              className="pt-status-sel pt-status-sel--mini"
              value={t.status}
              onChange={(e) => onStatusChange(t.id, e.target.value)}
              aria-label="Changer le statut"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MODALE CRÉATION / ÉDITION
   ════════════════════════════════════════════════════════ */
function TaskModal({ task, appId, team, campList, clubOptions, onClose }) {
  const editing = !!task;
  const [title, setTitle]           = useState(task?.title || task?.label || "");
  const [description, setDesc]      = useState(task?.description || "");
  const [status, setStatus]         = useState(normStatus(task?.status));
  const [priority, setPriority]     = useState(PRIORITIES.includes(task?.priority) ? task.priority : "Normale");
  const [assigneeId, setAssignee]   = useState(
    (Array.isArray(task?.assignedTo) && task.assignedTo[0]) || task?.assigneeId || ""
  );
  const [campagneId, setCampagne]   = useState(task?.campagneId ? String(task.campagneId) : "");
  const [deadline, setDeadline]     = useState(task?.deadline || "");
  const [selClubs, setSelClubs]     = useState(() => clubIdsOf(task || {}));
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);

  const toggleClub = (id) => setSelClubs((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );

  async function handleSave() {
    if (!title.trim()) { setError("Donne un titre à la tâche."); return; }
    setSaving(true); setError(null);
    const assignee = team.find((u) => String(u.id) === String(assigneeId) || String(u.appId) === String(assigneeId));
    const patch = {
      title: title.trim(),
      description,
      status,
      priority,
      assignedTo: assigneeId ? [assigneeId] : [],
      assigneeId: assigneeId || "",
      campagneId: campagneId || "",
      deadline: deadline || "",
      clubs: selClubs,
    };
    try {
      if (editing) await updateTask(String(task.id), patch, appId);
      else await createTask(patch, appId);
      onClose();
    } catch {
      setError("Enregistrement impossible. Vérifie ta connexion et réessaie.");
      setSaving(false);
    }
  }

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(26,26,28,.5)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000,
  };
  const card = {
    width: "min(520px,100%)", maxHeight: "90vh", overflowY: "auto",
    background: "#fff", borderRadius: 14, padding: "20px 22px",
    boxShadow: "0 16px 48px rgba(0,0,0,.24)", fontFamily: "inherit",
  };
  const label = { fontSize: 13, fontWeight: 600, color: "var(--text, #1a1a1c)", margin: "0 0 8px" };
  const field = { marginBottom: 16 };
  const optTag = { fontWeight: 400, color: "#94A3B8", fontSize: 12 };
  const inputBase = {
    width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #E2E8F0",
    fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: "#F8FAFC", outline: "none",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "var(--text, #1a1a1c)" }}>
            {editing ? "Modifier la tâche" : "Nouvelle tâche"}
          </h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94A3B8", lineHeight: 1 }}>×</button>
        </div>

        <div style={field}>
          <p style={label}>Titre</p>
          <input value={title} onChange={(e) => { setTitle(e.target.value); setError(null); }} style={inputBase} placeholder="Ex : Affiche tournoi P500" />
          {error && <p style={{ fontSize: 13, color: "#B23B3B", margin: "8px 0 0" }}>{error}</p>}
        </div>

        <div style={field}>
          <p style={label}>Détails <span style={optTag}>· optionnel</span></p>
          <textarea rows={2} value={description} onChange={(e) => setDesc(e.target.value)} style={{ ...inputBase, resize: "vertical" }} />
        </div>

        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Confier à <span style={optTag}>· optionnel</span></p>
            <select value={assigneeId || ""} onChange={(e) => setAssignee(e.target.value)} style={inputBase}>
              <option value="">Non assignée</option>
              {team.map((u) => {
                const id = u.id != null ? u.id : u.appId;
                return <option key={String(id)} value={String(id)}>{userLabel(u)}</option>;
              })}
            </select>
          </div>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Échéance <span style={optTag}>· optionnel</span></p>
            <input type="date" value={deadline || ""} onChange={(e) => setDeadline(e.target.value)} style={inputBase} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Statut</p>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputBase}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Priorité</p>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputBase}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={field}>
          <p style={label}>Campagne <span style={optTag}>· le « projet » · optionnel</span></p>
          <select value={campagneId || ""} onChange={(e) => setCampagne(e.target.value)} style={inputBase}>
            <option value="">Aucune</option>
            {campList.map((c) => <option key={String(c.id)} value={String(c.id)}>{c.name}</option>)}
          </select>
        </div>

        {clubOptions.length > 0 && (
          <div style={field}>
            <p style={label}>Clubs concernés <span style={optTag}>· optionnel</span></p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {clubOptions.map((c) => {
                const sel = selClubs.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleClub(c.id)}
                    style={{
                      padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                      border: sel ? `1.5px solid ${ACCENT}` : "1px solid #E2E8F0",
                      background: sel ? "#EFF6FF" : "#fff",
                      color: sel ? ACCENT : "var(--text, #1a1a1c)",
                    }}
                  >{c.name}</button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #CBD5E1", background: "transparent", fontSize: 14, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}>Annuler</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: saving ? "#94A3B8" : ACCENT, color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer la tâche"}
          </button>
        </div>
      </div>
    </div>
  );
}
